import React, { useState, useEffect } from 'react';
import { 
  Card, Table, Button, Badge, Modal, Form, Alert, Spinner, 
  ListGroup, Row, Col, Tabs, Tab, Accordion, ProgressBar 
} from 'react-bootstrap';
import RoleService from '../../../services/RoleService';
import AuthApiService from '../../../services/AuthApiService';

const RoleManagement = () => {
  // State
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [permissions, setPermissions] = useState({});
  const [roleStats, setRoleStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Modal states
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    permissions: []
  });
  
  // Active tab
  const [activeTab, setActiveTab] = useState('roles');

  // Fetch data on mount
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [rolesRes, usersRes, permsRes, statsRes] = await Promise.all([
        RoleService.getAllRoles(),
        AuthApiService.getUsers(),
        RoleService.getAllPermissions(),
        RoleService.getRoleStats()
      ]);
      
      setRoles(rolesRes.data || []);
      setUsers(usersRes.data?.data || []);
      setPermissions(permsRes.data || {});
      setRoleStats(statsRes.data || []);
    } catch (err) {
      setError(err.message || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      permissions: []
    });
    setSelectedRole(null);
  };

  // Open create role modal
  const openCreateModal = () => {
    resetForm();
    setShowRoleModal(true);
  };

  // Open edit role modal
  const openEditModal = (role) => {
    setSelectedRole(role);
    setFormData({
      name: role.name,
      description: role.description || '',
      permissions: role.permissions || []
    });
    setShowRoleModal(true);
  };

  // Open delete modal
  const openDeleteModal = (role) => {
    setSelectedRole(role);
    setShowDeleteModal(true);
  };

  // Open assign modal
  const openAssignModal = (user) => {
    setSelectedUser(user);
    setShowAssignModal(true);
  };

  // Handle form change
  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Handle permission toggle
  const togglePermission = (permKey) => {
    setFormData(prev => {
      const perms = [...prev.permissions];
      if (perms.includes(permKey)) {
        return { ...prev, permissions: perms.filter(p => p !== permKey) };
      } else {
        return { ...prev, permissions: [...perms, permKey] };
      }
    });
  };

  // Toggle all permissions in category
  const toggleCategory = (categoryPerms, selectAll) => {
    setFormData(prev => {
      const categoryKeys = categoryPerms.map(p => p.key);
      let newPerms;
      if (selectAll) {
        newPerms = [...new Set([...prev.permissions, ...categoryKeys])];
      } else {
        newPerms = prev.permissions.filter(p => !categoryKeys.includes(p));
      }
      return { ...prev, permissions: newPerms };
    });
  };

  // Save role (create or update)
  const handleSaveRole = async () => {
    try {
      if (!formData.name.trim()) {
        setError('Role name is required');
        return;
      }
      if (formData.permissions.length === 0) {
        setError('At least one permission is required');
        return;
      }

      if (selectedRole) {
        await RoleService.updateRole(selectedRole.id, formData);
        setSuccess('Role updated successfully');
      } else {
        await RoleService.createRole(formData);
        setSuccess('Role created successfully');
      }
      
      setShowRoleModal(false);
      resetForm();
      fetchData();
    } catch (err) {
      setError(err.message || 'Failed to save role');
    }
  };

  // Delete role
  const handleDeleteRole = async () => {
    try {
      await RoleService.deleteRole(selectedRole.id);
      setSuccess('Role deleted successfully');
      setShowDeleteModal(false);
      setSelectedRole(null);
      fetchData();
    } catch (err) {
      setError(err.message || 'Failed to delete role');
    }
  };

  // Assign role to user
  const handleAssignRole = async (roleId) => {
    try {
      await RoleService.assignRoleToUser(selectedUser.id, roleId);
      setSuccess(`Role assigned to ${selectedUser.username} successfully`);
      setShowAssignModal(false);
      setSelectedUser(null);
      fetchData();
    } catch (err) {
      setError(err.message || 'Failed to assign role');
    }
  };

  // Get permission count for a role
  const getPermissionCount = (role) => {
    return role.permissions?.length || 0;
  };

  // Get total permission count
  const getTotalPermissionCount = () => {
    return Object.values(permissions).flat().length;
  };

  // Get role badge color
  const getRoleBadgeColor = (roleName) => {
    const colors = {
      'Super Admin': 'danger',
      'Admin': 'warning',
      'Moderator': 'info',
      'Content Manager': 'primary',
      'User': 'secondary',
      'Viewer': 'light'
    };
    return colors[roleName] || 'secondary';
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <h2 className="mb-4">Role Management</h2>
      
      {error && (
        <Alert variant="danger" onClose={() => setError('')} dismissible>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert variant="success" onClose={() => setSuccess('')} dismissible>
          {success}
        </Alert>
      )}

      <Tabs
        activeKey={activeTab}
        onSelect={(k) => setActiveTab(k)}
        className="mb-4"
      >
        <Tab eventKey="roles" title="Roles">
          <Row>
            <Col md={8}>
              <Card>
                <Card.Header className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">System Roles</h5>
                  <Button variant="primary" size="sm" onClick={openCreateModal}>
                    <i className="fas fa-plus me-2"></i>Create Role
                  </Button>
                </Card.Header>
                <Card.Body>
                  <Table striped hover responsive>
                    <thead>
                      <tr>
                        <th>Role Name</th>
                        <th>Description</th>
                        <th>Type</th>
                        <th>Permissions</th>
                        <th>Users</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {roles.map(role => {
                        const stats = roleStats.find(s => s.id === role.id);
                        return (
                          <tr key={role.id}>
                            <td>
                              <Badge bg={getRoleBadgeColor(role.name)} className="me-2">
                                {role.name}
                              </Badge>
                            </td>
                            <td>{role.description || '-'}</td>
                            <td>
                              {role.is_system ? (
                                <Badge bg="dark">System</Badge>
                              ) : (
                                <Badge bg="light" text="dark">Custom</Badge>
                              )}
                            </td>
                            <td>
                              <Badge bg="info">
                                {getPermissionCount(role)} / {getTotalPermissionCount()}
                              </Badge>
                              <ProgressBar 
                                now={(getPermissionCount(role) / getTotalPermissionCount()) * 100} 
                                variant="info"
                                style={{ height: '4px', marginTop: '4px' }}
                              />
                            </td>
                            <td>
                              <Badge bg="secondary">{stats?.user_count || 0}</Badge>
                            </td>
                            <td>
                              <Button 
                                variant="outline-primary" 
                                size="sm" 
                                className="me-2"
                                onClick={() => openEditModal(role)}
                              >
                                <i className="fas fa-edit"></i>
                              </Button>
                              {!role.is_system && (
                                <Button 
                                  variant="outline-danger" 
                                  size="sm"
                                  onClick={() => openDeleteModal(role)}
                                >
                                  <i className="fas fa-trash"></i>
                                </Button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Col>

            <Col md={4}>
              <Card className="mb-3">
                <Card.Header>
                  <h6 className="mb-0">Role Statistics</h6>
                </Card.Header>
                <Card.Body>
                  <ListGroup variant="flush">
                    <ListGroup.Item className="d-flex justify-content-between">
                      <span>Total Roles</span>
                      <Badge bg="primary">{roles.length}</Badge>
                    </ListGroup.Item>
                    <ListGroup.Item className="d-flex justify-content-between">
                      <span>System Roles</span>
                      <Badge bg="dark">{roles.filter(r => r.is_system).length}</Badge>
                    </ListGroup.Item>
                    <ListGroup.Item className="d-flex justify-content-between">
                      <span>Custom Roles</span>
                      <Badge bg="light" text="dark">
                        {roles.filter(r => !r.is_system).length}
                      </Badge>
                    </ListGroup.Item>
                  </ListGroup>
                </Card.Body>
              </Card>

              <Card>
                <Card.Header>
                  <h6 className="mb-0">Available Permission Categories</h6>
                </Card.Header>
                <Card.Body>
                  <ListGroup variant="flush">
                    {Object.entries(permissions).map(([category, perms]) => (
                      <ListGroup.Item 
                        key={category} 
                        className="d-flex justify-content-between align-items-center"
                      >
                        <span>
                          <i className={`fas fa-${getCategoryIcon(category)} me-2 text-muted`}></i>
                          {category}
                        </span>
                        <Badge bg="secondary" pill>{perms.length}</Badge>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Tab>

        <Tab eventKey="users" title="User Assignments">
          <Card>
            <Card.Header>
              <h5 className="mb-0">User Role Assignments</h5>
            </Card.Header>
            <Card.Body>
              <Table striped hover responsive>
                <thead>
                  <tr>
                    <th>Username</th>
                    <th>Email</th>
                    <th>Current Role</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(user => (
                    <tr key={user.id}>
                      <td>{user.username}</td>
                      <td>{user.email}</td>
                      <td>
                        <Badge bg={getRoleBadgeColor(user.role)}>
                          {user.role}
                        </Badge>
                      </td>
                      <td>
                        {user.is_active ? (
                          <Badge bg="success">Active</Badge>
                        ) : (
                          <Badge bg="danger">Inactive</Badge>
                        )}
                      </td>
                      <td>
                        <Button 
                          variant="info" 
                          size="sm"
                          onClick={() => openAssignModal(user)}
                        >
                          <i className="fas fa-user-tag me-2"></i>
                          Change Role
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Tab>
      </Tabs>

      {/* Create/Edit Role Modal */}
      <Modal 
        show={showRoleModal} 
        onHide={() => setShowRoleModal(false)}
        size="lg"
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedRole ? 'Edit Role' : 'Create New Role'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Role Name <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                value={formData.name}
                onChange={(e) => handleFormChange('name', e.target.value)}
                placeholder="Enter role name"
                disabled={selectedRole?.is_system}
              />
              {selectedRole?.is_system && (
                <Form.Text className="text-muted">
                  System role names cannot be changed
                </Form.Text>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={formData.description}
                onChange={(e) => handleFormChange('description', e.target.value)}
                placeholder="Enter role description"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                Permissions <span className="text-danger">*</span>
                <Badge bg="info" className="ms-2">
                  {formData.permissions.length} selected
                </Badge>
              </Form.Label>
              
              <Accordion defaultActiveKey={Object.keys(permissions)[0]}>
                {Object.entries(permissions).map(([category, perms], idx) => {
                  const categorySelected = perms.filter(p => 
                    formData.permissions.includes(p.key)
                  ).length;
                  const allSelected = categorySelected === perms.length;
                  
                  return (
                    <Accordion.Item eventKey={category} key={category}>
                      <Accordion.Header>
                        <div className="d-flex justify-content-between align-items-center w-100 me-3">
                          <span>
                            <i className={`fas fa-${getCategoryIcon(category)} me-2`}></i>
                            {category}
                          </span>
                          <div onClick={(e) => e.stopPropagation()}>
                            <Form.Check
                              type="checkbox"
                              label={`${categorySelected}/${perms.length}`}
                              checked={allSelected}
                              onChange={(e) => toggleCategory(perms, e.target.checked)}
                              className="ms-3"
                            />
                          </div>
                        </div>
                      </Accordion.Header>
                      <Accordion.Body>
                        <Row>
                          {perms.map(perm => (
                            <Col md={6} key={perm.key} className="mb-2">
                              <Form.Check
                                type="checkbox"
                                id={perm.key}
                                label={perm.label}
                                checked={formData.permissions.includes(perm.key)}
                                onChange={() => togglePermission(perm.key)}
                              />
                            </Col>
                          ))}
                        </Row>
                      </Accordion.Body>
                    </Accordion.Item>
                  );
                })}
              </Accordion>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRoleModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSaveRole}>
            {selectedRole ? 'Update Role' : 'Create Role'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Role Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Delete Role</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete the role <strong>{selectedRole?.name}</strong>?</p>
          <Alert variant="warning">
            <i className="fas fa-exclamation-triangle me-2"></i>
            This action cannot be undone. Users with this role will need to be reassigned.
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteRole}>
            Delete Role
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Assign Role Modal */}
      <Modal show={showAssignModal} onHide={() => setShowAssignModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Assign Role to {selectedUser?.username}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group>
              <Form.Label>Select Role</Form.Label>
              <Form.Select
                value={selectedUser?.role_id || ''}
                onChange={(e) => handleAssignRole(parseInt(e.target.value))}
              >
                <option value="">Choose a role...</option>
                {roles.map(role => (
                  <option key={role.id} value={role.id}>
                    {role.name} {role.is_system ? '(System)' : '(Custom)'}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Form>
          
          {selectedUser && (
            <div className="mt-3">
              <h6>Current Role Permissions:</h6>
              <ListGroup variant="flush">
                {roles
                  .find(r => r.name === selectedUser.role)
                  ?.permissions?.slice(0, 5)
                  .map((perm, idx) => (
                    <ListGroup.Item key={idx} className="py-1">
                      <small>• {perm}</small>
                    </ListGroup.Item>
                  ))}
              </ListGroup>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAssignModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

// Helper function to get icon for category
function getCategoryIcon(category) {
  const icons = {
    'Streams': 'video',
    'Users': 'users',
    'VOD': 'film',
    'IPTV': 'tv',
    'CDN': 'server',
    'Domains': 'globe',
    'Tickets': 'ticket-alt',
    'Billing': 'credit-card',
    'System': 'cog'
  };
  return icons[category] || 'check';
}

export default RoleManagement;
